import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, COMPANY, NAV_LINKS } from "@/lib/siteData";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex h-28 lg:h-32 items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="navbar-logo-link"
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src={LOGO_URL}
              alt={COMPANY.name}
              className="h-20 lg:h-28 w-auto object-contain"
              data-testid="navbar-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-navy">
                RPG Rasayan
              </span>
              <span className="text-[12px] lg:text-sm uppercase tracking-[0.2em] text-slate-500">
                Udyog Ltd
              </span>
            </div>
            <span className="sr-only">{COMPANY.name}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  [
                    "text-[13px] font-semibold uppercase tracking-wider transition-colors",
                    isActive
                      ? "text-indigo-600"
                      : "text-slate-900 hover:text-indigo-600",
                  ].join(" ")
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/quote" data-testid="navbar-quote-cta-link">
              <Button
                data-testid="navbar-quote-cta"
                className="gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90 transition-opacity h-10 px-5"
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md text-slate-900 hover:bg-slate-100"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden pb-5 border-t border-slate-100 mt-0"
            data-testid="navbar-mobile-menu"
          >
            <nav className="flex flex-col gap-1 pt-3" aria-label="Mobile">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    [
                      "px-3 py-3 text-sm font-semibold uppercase tracking-wider rounded-md",
                      isActive
                        ? "text-indigo-600 bg-indigo-50"
                        : "text-slate-900 hover:bg-slate-50",
                    ].join(" ")
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                data-testid="navbar-mobile-quote-cta-link"
                className="mt-3"
              >
                <Button
                  data-testid="navbar-mobile-quote-cta"
                  className="w-full gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90"
                >
                  Get a Quote
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
